import { AsyncPipe, DOCUMENT, Location, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
  Renderer2,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import { map, Observable, of, switchMap } from 'rxjs';
import { FooterComponent } from 'src/app/_shared/components/footer/footer.component';
import { HeaderComponent } from 'src/app/_shared/components/header/header.component';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { municipalities, places, regions } from 'utils/places-utility';
import { RouteTranslatePipe } from '../../../_shared/pipes/route-translate.pipe';
import { GetIndexCategoriesGQL } from '../search-results.query.generated';

@Pipe({
  name: 'filterIndex',
  standalone: true,
})
export class FilterIndexPipe implements PipeTransform {
  transform(value: Index[], search: string): Index[] {
    if (!value?.length) return [];
    if ((search || '').length < 2) return value;
    return value.filter(v =>
      v.value
        .replaceAll(',', '')
        .replaceAll(/\s+/g, '-')
        .toLowerCase()
        .includes(search.replaceAll(',', '').replaceAll(/\s+/g, '-').toLowerCase()),
    );
  }
}

@Component({
  standalone: true,
  selector: 'sharry-index',
  templateUrl: './index.component.html',
  styleUrl: './index.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeaderComponent,
    FooterComponent,
    I18NextModule,
    MatInputModule,
    RouterLink,
    AsyncPipe,
    RouteTranslatePipe,
    FilterIndexPipe,
    FormsModule,
    NgTemplateOutlet,
  ],
})
export class IndexComponent implements OnInit, OnDestroy {
  activeRoute: string;
  list$: Observable<Index[] | CategoryIndex[]>;
  search: string;
  timeout: NodeJS.Timeout;
  indexTitle: string;

  constructor(
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private getIndexCategoriesGQL: GetIndexCategoriesGQL,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private location: Location,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
  ) {
    this.metadataService.clean();
  }

  ngOnInit() {
    this.list$ = this.route.data.pipe(
      map(d => d.indexType),
      switchMap(indexType => {
        this.metadataService.setMetadataPage(indexType, this.location.path());
        switch (indexType) {
          case IndexType.Municipality:
            this.activeRoute = 'find/what-to-do-near';
            this.indexTitle = 'municipality';
            this.cd.detectChanges();
            return of(
              Object.entries(municipalities)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => ({
                  value: value,
                  key: key,
                })),
            );
          case IndexType.RegionAll:
            this.activeRoute = 'find/what-to-do-in';
            this.indexTitle = 'region';
            this.cd.detectChanges();
            return of(
              Object.entries(regions)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => ({
                  value: value,
                  key: key,
                })),
            );
          case IndexType.RegionWonder:
            this.activeRoute = 'find/what-to-visit-in';
            this.indexTitle = 'region-wonder';
            this.cd.detectChanges();
            return of(
              Object.entries(regions)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => ({
                  value: value,
                  key: key,
                })),
            );
          case IndexType.RegionEventSharry:
            this.activeRoute = 'find/events-in';
            this.indexTitle = 'events-in';
            this.cd.detectChanges();
            return of(
              Object.entries(regions)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => ({
                  value: value,
                  key: key,
                })),
            );
          case IndexType.ProvinceAll:
            this.activeRoute = 'find/what-to-do-at';
            this.indexTitle = 'province-all';
            this.cd.detectChanges();
            return of(
              Object.entries(places)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => ({
                  value: value,
                  key: key,
                })),
            );
          case IndexType.ProvinceWonder:
            this.activeRoute = 'find/what-to-visit-at';
            this.indexTitle = 'province-wonder';
            this.cd.detectChanges();
            return of(
              Object.entries(places)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => ({
                  value: value,
                  key: key,
                })),
            );
          case IndexType.ProvinceEventSharry:
            this.activeRoute = 'find/events-at';
            this.indexTitle = 'events-at';
            this.cd.detectChanges();
            return of(
              Object.entries(places)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => ({
                  value: value,
                  key: key,
                })),
            );
          case IndexType.Category:
            this.activeRoute = 'category';
            this.indexTitle = 'category';
            this.cd.detectChanges();
            return this.getIndexCategoriesGQL
              .fetch()
              .pipe(
                map(r =>
                  [...r.data.results]
                    .sort((a, b) => a.normalizedName.localeCompare(b.normalizedName))
                    .map(c => ({ value: c.name, key: c.normalizedName, icon: c.icon.url })),
                ),
              );
          default:
            this.activeRoute = '';
            this.cd.detectChanges();
            return [];
        }
      }),
    );
    this.renderer.addClass(this.document.querySelector('html'), 'shrink');
  }

  onModelChange(value: string) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.search = value;
      this.cd.detectChanges();
    }, 500);
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
    this.metadataService.clean();
  }
}

type Index = { key: string; value: string };
type CategoryIndex = { key: string; value: string; icon: string };

export enum IndexType {
  Municipality = 'municipality',
  RegionAll = 'regionAll',
  RegionWonder = 'regionWonder',
  RegionEventSharry = 'regionEventSharry',
  ProvinceAll = 'provinceAll',
  ProvinceWonder = 'provinceWonder',
  ProvinceEventSharry = 'provinceEventSharry',
  Category = 'category',
}
